import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { freezeObject, objectHasDifference } from '../helper/functions';

export interface StepperItem {
  id: number;
  label: string;
  /**
   * 0 - default
   *
   * 1 - selected
   *
   * 2 - applicable
   *
   * 3 - non applicable
   */
  status: number;
  src: string;
}

export const defaultStepper = [
  {
    id: 1,
    label: 'Liquidation Risk',
    status: 1 as StepperItem['status'],
    src: '/icons/calculator.svg',
  },
  {
    id: 2,
    label: 'Funding Risk',
    status: 0 as StepperItem['status'],
    src: '/icons/calculator.svg',
  },
  {
    id: 3,
    label: 'Credit Risk',
    status: 0 as StepperItem['status'],
    src: '/icons/calculator.svg',
  },
  {
    id: 4,
    label: 'Comodity Risk',
    status: 0 as StepperItem['status'],
    src: '/icons/form.svg',
  },
  {
    id: 5,
    label: 'Currency Risk',
    status: 0 as StepperItem['status'],
    src: '/icons/form.svg',
  },
  {
    id: 6,
    label: 'Interest Risk',
    status: 0 as StepperItem['status'],
    src: '/icons/form.svg',
  },
  {
    id: 7,
    label: 'Operation Risk',
    status: 0 as StepperItem['status'],
    src: '/icons/form.svg',
  },
  {
    id: 8,
    label: 'Disaster Risk',
    status: 0 as StepperItem['status'],
    src: '/icons/form.svg',
  },
  {
    id: 9,
    label: 'Compliance Risk',
    status: 0 as StepperItem['status'],
    src: '/icons/form.svg',
  },
  {
    id: 10,
    label: 'Legal Risk',
    status: 0 as StepperItem['status'],
    src: '/icons/form.svg',
  },
];

export interface StepperStore {
  data: StepperItem[];
  currentItem: StepperItem;
  setStepper: (
    _direction: 'next' | 'prev' | 'goto',
    _targetId?: number,
    _completed?: boolean
  ) => void;
  resetStepper: () => void;
  formDirty: (_: StepperItem[]) => boolean;
}

const useStepperStore = create<StepperStore>()(
  persist(
    (set, get) => ({
      data: defaultStepper,
      currentItem: defaultStepper[0],
      setStepper: (
        direction: 'next' | 'prev' | 'goto',
        targetId?: number,
        completed?: boolean
      ) => {
        const state = get();
        const { data, currentItem } = state;
        let currentIndex = data.findIndex((v) => v.id === currentItem?.id);

        if (direction === 'goto' && targetId) {
          currentIndex = data.findIndex((v) => v.id === targetId);
        } else if (direction === 'next') {
          currentIndex = Math.min(currentIndex + 1, data.length - 1);
        } else if (direction === 'prev') {
          currentIndex = Math.max(currentIndex - 1, 0);
        }

        const newData = data.map((item, index) => {
          if (index === currentIndex) {
            return { ...item, status: item.status >= 1 ? item.status : 1 }; // selected
          }

          if (item.id === currentItem?.id) {
            return {
              ...item,
              status: completed ? 3 : completed == undefined ? 0 : 2, // mark previous as tick or untick
            };
          }

          return item;
        });

        set({
          data: newData,
          currentItem: newData[currentIndex],
        });
      },
      resetStepper: () =>
        set({
          data: freezeObject(defaultStepper),
          currentItem:
            defaultStepper.find((i) => i.status == 1) || defaultStepper[0],
        }),
      formDirty: (value: StepperItem[]) => {
        return objectHasDifference(defaultStepper, value);
      },
    }),
    {
      name: 'stepper-store',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export default useStepperStore;
